import React, { useState, useEffect } from "react";
import axios from "axios";
import { Plus, PencilSimple, Trash, ArrowsClockwise, ToggleLeft, ToggleRight, Lightning, Info } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const CATEGORIES = [
  "Sosyal Medya Yönetimi",
  "Reklam Kampanyası",
  "Tasarım Hizmetleri",
  "Danışmanlık",
  "Diğer",
];

const fmt = (n) => `₺${(n || 0).toLocaleString("tr-TR", { maximumFractionDigits: 0 })}`;

const RecurringPaymentsPage = () => {
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    customer_id: "",
    category: "",
    amount: "",
    day_of_month: 1,
    active: true,
    invoice_status: "faturasız",
    description: "",
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [r, c] = await Promise.all([
        axios.get(`${API_URL}/api/recurring-payments`, { withCredentials: true }),
        axios.get(`${API_URL}/api/customers`, { withCredentials: true }),
      ]);
      setItems(r.data);
      setCustomers(c.data);
    } catch (error) {
      toast.error("Veriler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount),
        day_of_month: parseInt(formData.day_of_month, 10),
      };
      if (editing) {
        await axios.put(`${API_URL}/api/recurring-payments/${editing.id}`, payload, { withCredentials: true });
        toast.success("Tekrarlayan ödeme güncellendi");
      } else {
        await axios.post(`${API_URL}/api/recurring-payments`, payload, { withCredentials: true });
        toast.success("Tekrarlayan ödeme eklendi");
      }
      fetchData();
      handleClose();
    } catch (error) {
      toast.error("Kaydedilemedi");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu tekrarlayan ödemeyi silmek istediğinizden emin misiniz? Geçmiş işlemler etkilenmeyecek.")) return;
    try {
      await axios.delete(`${API_URL}/api/recurring-payments/${id}`, { withCredentials: true });
      toast.success("Silindi");
      fetchData();
    } catch (error) {
      toast.error("Silinemedi");
    }
  };

  const handleToggle = async (item) => {
    try {
      await axios.put(`${API_URL}/api/recurring-payments/${item.id}`, { active: !item.active }, { withCredentials: true });
      fetchData();
    } catch (error) {
      toast.error("Güncellenemedi");
    }
  };

  const handleGenerateNow = async () => {
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/recurring-payments/generate-now`, {}, { withCredentials: true });
      if (data.created > 0) {
        toast.success(`${data.created} yeni işlem oluşturuldu`);
      } else {
        toast.info("Bu ay için yeni işlem oluşturulacak tekrarlayan ödeme yok");
      }
      fetchData();
    } catch (error) {
      toast.error("İşlem oluşturulamadı");
    } finally {
      setGenerating(false);
    }
  };

  const handleEdit = (item) => {
    setEditing(item);
    setFormData({
      customer_id: item.customer_id,
      category: item.category,
      amount: item.amount.toString(),
      day_of_month: item.day_of_month,
      active: item.active,
      invoice_status: item.invoice_status,
      description: item.description || "",
    });
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setEditing(null);
    setFormData({
      customer_id: "",
      category: "",
      amount: "",
      day_of_month: 1,
      active: true,
      invoice_status: "faturasız",
      description: "",
    });
  };

  const totalMonthly = items.filter((i) => i.active).reduce((s, i) => s + i.amount, 0);
  const totalYearly = totalMonthly * 12;
  const activeCount = items.filter((i) => i.active).length;

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen"><div className="inline-block h-10 w-10 animate-spin rounded-full border-2 border-[hsl(var(--border))] border-t-[hsl(var(--text-strong))]"></div></div>;
  }

  return (
    <div className="p-8" data-testid="recurring-page">
      <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tighter text-[hsl(var(--text-strong))] mb-2">Tekrarlayan Ödemeler</h1>
          <p className="text-sm text-[hsl(var(--text-soft))] font-light">Müşterileriniz için aylık otomatik gelir tanımlayın. Her ay başında otomatik olarak "Bekleyen" işlem oluşturulur.</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleGenerateNow}
            disabled={generating}
            data-testid="generate-now-button"
            className="flex items-center gap-2 px-4 py-3 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface-elevated))] text-[hsl(var(--text-strong))] font-medium text-sm hover:bg-[hsl(var(--secondary))] disabled:opacity-50"
          >
            <Lightning size={16} weight="duotone" />
            {generating ? "Üretiliyor..." : "Şimdi Üret"}
          </button>
          <button
            onClick={() => setShowModal(true)}
            data-testid="add-recurring-button"
            className="flex items-center gap-2 px-5 py-3 rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-medium text-sm shadow-premium hover:opacity-90 premium-hover"
          >
            <Plus size={18} weight="bold" />
            Yeni Tekrarlayan
          </button>
        </div>
      </div>

      {/* Info banner */}
      <div className="p-4 mb-6 rounded-2xl bg-[hsl(var(--secondary))]/40 border border-[hsl(var(--border))] flex items-start gap-3">
        <Info size={18} weight="duotone" className="text-[hsl(var(--text-soft))] flex-shrink-0 mt-0.5" />
        <p className="text-xs text-[hsl(var(--text-soft))] leading-relaxed">
          Tekrarlayan ödemeler her ay ayarladığınız günde otomatik olarak <strong className="text-[hsl(var(--text-strong))]">Gelirler</strong> sayfasında "Bekleyen" statüsünde görünür. Müşteri ödeme yaptığında "Ödendi" olarak işaretleyebilirsiniz. Tekrarlayan ödemeyi silmek geçmişte oluşturulmuş işlemleri etkilemez.
        </p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="p-6 rounded-2xl bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] shadow-premium-sm">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[hsl(var(--text-soft))]">Aktif Tekrarlayan</p>
            <ArrowsClockwise size={20} weight="duotone" className="text-[hsl(var(--text-soft))]" />
          </div>
          <p className="text-3xl font-display font-bold text-[hsl(var(--text-strong))] tabular-nums">{activeCount}</p>
          <p className="text-xs text-[hsl(var(--text-faint))] mt-1">Toplam {items.length} kayıt</p>
        </div>
        <div className="p-6 rounded-2xl bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] shadow-premium-sm">
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[hsl(var(--text-soft))] mb-2">Aylık Gelir Tahmini</p>
          <p className="text-3xl font-display font-bold text-[hsl(var(--success))] tabular-nums">{fmt(totalMonthly)}</p>
        </div>
        <div className="p-6 rounded-2xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] shadow-premium">
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] opacity-70 mb-2">Yıllık Projeksiyon</p>
          <p className="text-3xl font-display font-bold tabular-nums">{fmt(totalYearly)}</p>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-2xl bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] overflow-hidden shadow-premium-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-[hsl(var(--secondary))] border-b border-[hsl(var(--border))]">
                <th className="text-left px-5 py-3.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[hsl(var(--text-soft))]">Müşteri</th>
                <th className="text-left px-5 py-3.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[hsl(var(--text-soft))]">Kategori</th>
                <th className="text-center px-5 py-3.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[hsl(var(--text-soft))]">Ödeme Günü</th>
                <th className="text-right px-5 py-3.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[hsl(var(--text-soft))]">Tutar</th>
                <th className="text-left px-5 py-3.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[hsl(var(--text-soft))]">Son Üretim</th>
                <th className="text-center px-5 py-3.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[hsl(var(--text-soft))]">Durum</th>
                <th className="text-right px-5 py-3.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[hsl(var(--text-soft))]">İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan={7} className="px-5 py-16 text-center">
                  <div className="text-sm text-[hsl(var(--text-faint))] mb-3">Henüz tekrarlayan ödeme tanımlanmamış</div>
                  <button onClick={() => setShowModal(true)} className="text-sm text-[hsl(var(--text-strong))] underline font-medium">İlk tekrarlayan ödemeyi ekleyin</button>
                </td></tr>
              ) : items.map((it, idx) => (
                <tr key={it.id} className={`border-b border-[hsl(var(--border))] hover:bg-[hsl(var(--secondary))]/40 ${!it.active ? 'opacity-50' : ''}`} data-testid={`recurring-row-${idx}`}>
                  <td className="px-5 py-3.5 text-sm font-medium text-[hsl(var(--text-strong))]">{it.customer_name || "-"}</td>
                  <td className="px-5 py-3.5 text-sm text-[hsl(var(--text-soft))]">{it.category}</td>
                  <td className="px-5 py-3.5 text-sm text-center text-[hsl(var(--text-soft))] tabular-nums">Ayın {it.day_of_month}.</td>
                  <td className="px-5 py-3.5 text-sm text-right font-semibold text-[hsl(var(--text-strong))] tabular-nums">{fmt(it.amount)}</td>
                  <td className="px-5 py-3.5 text-xs text-[hsl(var(--text-faint))] tabular-nums">{it.last_generated_month || "—"}</td>
                  <td className="px-5 py-3.5 text-center">
                    <button onClick={() => handleToggle(it)} data-testid={`toggle-recurring-${idx}`}>
                      {it.active ? (
                        <ToggleRight size={28} weight="fill" className="text-[hsl(var(--success))]" />
                      ) : (
                        <ToggleLeft size={28} weight="fill" className="text-[hsl(var(--text-faint))]" />
                      )}
                    </button>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex justify-end gap-1">
                      <button onClick={() => handleEdit(it)} data-testid={`edit-recurring-${idx}`} className="p-2 rounded-lg hover:bg-[hsl(var(--secondary))] text-[hsl(var(--text-soft))] hover:text-[hsl(var(--text-strong))]">
                        <PencilSimple size={14} weight="bold" />
                      </button>
                      <button onClick={() => handleDelete(it.id)} data-testid={`delete-recurring-${idx}`} className="p-2 rounded-lg hover:bg-[hsl(var(--danger))]/10 text-[hsl(var(--text-soft))] hover:text-[hsl(var(--danger))]">
                        <Trash size={14} weight="bold" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="rounded-2xl border-[hsl(var(--border))] bg-[hsl(var(--surface-elevated))] max-w-md" data-testid="recurring-modal">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold tracking-tight text-[hsl(var(--text-strong))]">
              {editing ? "Tekrarlayan Ödemeyi Düzenle" : "Yeni Tekrarlayan Ödeme"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="recurring-form">
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-[hsl(var(--text-soft))] mb-2">Müşteri *</label>
              <select value={formData.customer_id} onChange={(e) => setFormData({ ...formData, customer_id: e.target.value })} required data-testid="recurring-customer-select" className="w-full px-3 py-2.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-sm text-[hsl(var(--text-strong))]">
                <option value="">Seçiniz</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-[hsl(var(--text-soft))] mb-2">Kategori *</label>
              <select value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} required data-testid="recurring-category-select" className="w-full px-3 py-2.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-sm text-[hsl(var(--text-strong))]">
                <option value="">Seçiniz</option>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-[hsl(var(--text-soft))] mb-2">Aylık Tutar *</label>
                <input type="number" step="0.01" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} required data-testid="recurring-amount-input" className="w-full px-3 py-2.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-sm text-[hsl(var(--text-strong))] tabular-nums" />
              </div>
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-[hsl(var(--text-soft))] mb-2">Ödeme Günü *</label>
                <input type="number" min={1} max={31} value={formData.day_of_month} onChange={(e) => setFormData({ ...formData, day_of_month: e.target.value })} required data-testid="recurring-day-input" className="w-full px-3 py-2.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-sm text-[hsl(var(--text-strong))] tabular-nums" />
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-[hsl(var(--text-soft))] mb-2">Fatura Durumu</label>
              <select value={formData.invoice_status} onChange={(e) => setFormData({ ...formData, invoice_status: e.target.value })} data-testid="recurring-invoice-select" className="w-full px-3 py-2.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-sm text-[hsl(var(--text-strong))]">
                <option value="faturalı">Faturalı</option>
                <option value="faturasız">Faturasız</option>
              </select>
            </div>

            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-[hsl(var(--text-soft))] mb-2">Açıklama</label>
              <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={2} placeholder="Örn: Aylık sosyal medya yönetimi" data-testid="recurring-description-input" className="w-full px-3 py-2.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-sm text-[hsl(var(--text-strong))]" />
            </div>

            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={formData.active} onChange={(e) => setFormData({ ...formData, active: e.target.checked })} data-testid="recurring-active-checkbox" className="w-4 h-4 rounded border-[hsl(var(--border))]" />
              <span className="text-sm text-[hsl(var(--text-strong))]">Aktif (her ay otomatik üret)</span>
            </label>

            <div className="flex gap-2 pt-3">
              <button type="button" onClick={handleClose} data-testid="cancel-recurring-button" className="flex-1 px-4 py-2.5 rounded-lg border border-[hsl(var(--border))] text-sm font-medium text-[hsl(var(--text-soft))] hover:bg-[hsl(var(--secondary))]">İptal</button>
              <button type="submit" data-testid="save-recurring-button" className="flex-1 px-4 py-2.5 rounded-lg bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-sm font-semibold hover:opacity-90 shadow-premium-sm">Kaydet</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RecurringPaymentsPage;
